// Small, accessible form primitives used by the controls panel (DXcompanion style:
// compact rows, muted labels, accent sliders, monospace accent section titles).

import { useId, type ReactNode } from 'react';

interface Base {
  label: string;
  hint: string;
}

function Hint({ id, text }: { id: string; text: string }) {
  return (
    <span id={id} className="hint">
      {text}
    </span>
  );
}

export function Select<T extends string | number>({
  label,
  hint,
  value,
  options,
  onChange,
}: Base & { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-0.5" title={hint}>
      <label htmlFor={id} className="label">
        {label}
      </label>
      <select
        id={id}
        aria-describedby={`${id}-hint`}
        className="field"
        value={String(value)}
        onChange={(e) => {
          const opt = options.find((o) => String(o.value) === e.target.value);
          if (opt) onChange(opt.value);
        }}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
      <Hint id={`${id}-hint`} text={hint} />
    </div>
  );
}

export function NumberField({
  label,
  hint,
  value,
  min,
  max,
  step = 1,
  unit,
  slider = true,
  onChange,
}: Base & {
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  slider?: boolean;
  onChange: (v: number) => void;
}) {
  const id = useId();
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <div className="flex flex-col gap-0.5" title={hint}>
      <label htmlFor={id} className="label flex justify-between">
        <span>{label}</span>
        {unit && <span className="opacity-70">{unit}</span>}
      </label>
      <div className="flex items-center gap-2">
        {slider && (
          <input
            type="range"
            aria-hidden="true"
            tabIndex={-1}
            className="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(clamp(Number(e.target.value)))}
          />
        )}
        <input
          id={id}
          type="number"
          aria-describedby={`${id}-hint`}
          className={`field font-mono ${slider ? 'w-[4.5rem]' : ''}`}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!Number.isNaN(v) && e.target.value !== '') onChange(clamp(v));
          }}
        />
      </div>
      <Hint id={`${id}-hint`} text={hint} />
    </div>
  );
}

export function Toggle({
  label,
  hint,
  checked,
  onChange,
}: Base & { checked: boolean; onChange: (v: boolean) => void }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2" title={hint}>
      <input
        id={id}
        type="checkbox"
        aria-describedby={`${id}-hint`}
        className="mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div>
        <label htmlFor={id} className="text-[12px]">
          {label}
        </label>
        <Hint id={`${id}-hint`} text={hint} />
      </div>
    </div>
  );
}

export function Group({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details open={defaultOpen} className="group border-b border-line">
      <summary className="legend flex cursor-pointer select-none items-center justify-between px-3 py-2 hover:bg-panel2">
        {title}
        <span aria-hidden="true" className="transition-transform group-open:rotate-90">
          ›
        </span>
      </summary>
      <div className="flex flex-col gap-2.5 px-3 pb-3 pt-1">{children}</div>
    </details>
  );
}
